import { defineStore } from "pinia";
import { Attribute } from "./entity";
import { AttributeList } from "./list.gen";
import type { AttributeStore, AttributeStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const attributeList = new AttributeList();
export const useAttributeStore = defineStore("attributeStore", {
  state: (): AttributeStoreState => ({
    activeID: null,
    cache: {},
    list: [] as Attribute[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-Attribute", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.Attribute"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(attribute: Attribute): Promise<void> {
      // Set active
      await attribute
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = attribute.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(attribute: Attribute): void {
      const id = attribute.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(attribute);

        // Set active
        if (this.active === null) {
          this.activeID = attribute.id;
        }
      } else {
        this.reinsert(attribute);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as Attribute[]).map((x: any) => {
        const attribute = new Attribute({});
        attribute.set(x as JSONData);
        return attribute;
      });
    },
    async delete(attribute: Attribute): Promise<void> {
      await attribute
        .delete()
        .then(() => {
          this.remove(attribute);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const attribute = (this.list as Attribute[]).find(
        (x: Attribute) => x.id === id,
      );
      if (attribute !== undefined) {
        await this.delete(attribute);
      } else {
        throw new Error("Could not find Attribute with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as Attribute[]).some((x: Attribute) => x.id === id);
    },
    get(id: string): Attribute | null {
      return (
        (this.list as Attribute[]).find((x: Attribute) => x.id === id) || null
      );
    },
    reinsert(attribute: Attribute): void {
      const index = (this.list as Attribute[]).findIndex(
        (x: Attribute) => x.id === attribute.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          attribute,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(attribute);
      }
      this.updateKey += 1;
    },
    remove({ id }: Attribute): void {
      const index = (this.list as Attribute[]).findIndex(
        (x: Attribute) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<Attribute> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const attribute = this.get(id);
        if (attribute !== null) {
          return attribute;
        }
      }

      // Retrieve attribute
      const attribute = new Attribute({});

      // Get attribute
      await attribute.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate attribute
        await this.activate(entity);
      });

      return attribute;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: Attribute[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as Attribute[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await attributeList.getAll().then(async (list) => {
        list.forEach((attribute) => {
          this.reinsert(attribute);
        });

        // Activate attribute
        if (list.length > 0) {
          if (attributeList.active !== null) {
            this.activeID = attributeList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("attribute_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as Attribute[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Attribute[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as Attribute),
        };
      }

      // Retrieve list
      const items = await attributeList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((attribute) => {
            this.reinsert(attribute);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Attribute) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Attribute[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as Attribute),
        };
      }

      // Retrieve list
      const items = await attributeList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((attribute) => {
            this.reinsert(attribute);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Attribute) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Attribute[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as Attribute),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await attributeList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((attribute) => {
            this.reinsert(attribute);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Attribute) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(attribute: Attribute): Promise<string> {
      // Update attribute
      await attribute.update();

      // Reinsert attribute
      this.reinsert(attribute);

      // Activate attribute
      if (this.active === null) {
        await this.activate(attribute);
      }

      this.emitStoreUpdateEvent();

      return attribute.id;
    },
  },
  getters: {
    active(): Attribute | null {
      if (this.activeID === null) {
        return null;
      }

      const attribute = (this.list as Attribute[]).find(
        (x: Attribute) => x.id === this.activeID,
      );
      if (attribute === undefined) {
        return null;
      }

      return attribute;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createAttributeStore(): AttributeStore {
  const store = useAttributeStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AttributeStore; // TODO: Fix this
}
